import { motion } from "framer-motion";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useProfile } from "../../contexts/ProfileContext.tsx";
import { useTabela } from "../../contexts/Tabela.js";
import { useToast } from "../../contexts/ToastContext.tsx";
import Separator from "../../core/components/Separator.tsx";
import Modal from "../../core/feedback/Modal.tsx";
import { isDataDoisMil, removeSeconds } from "../../core/utils/UtilityDates.ts";
import useService from "../../hooks/useService.ts";
import {
	LancarDatasDTO,
	lancarDatasEmbarque,
} from "../../services/ModuloGerenciador/Viagens.ts";
import CustomButtonV2 from "../CustomButtonV2/CustomButtonV2.js";
import "./ModalEditarDatas.css";

interface ModalEditarDatasProps {
	open: boolean;
	onClose: () => void;
	onSuccess: () => void;
}

function isDateTimeInRange(dateTimeStr) {
	if (!dateTimeStr) return false;
	const date = new Date(dateTimeStr);
	const min = new Date("1900-01-01T00:00");
	const max = new Date("2050-12-31T23:59");
	return date >= min && date <= max;
}

function ModalEditarDatas({ open, onClose, onSuccess }: ModalEditarDatasProps) {
	const { showToast } = useToast();
	const { profile } = useProfile();

	const { tabelaRedux } = useTabela();

	const [ranges, setRanges] = useState({
		previsaoEmbarque: true,
		dataHoraChegadaEmbarque: true,
		dataHoraEmbarcado: true,
		previsaoEntrega: true,
		dataHoraChegadaEntrega: true,
		dataHoraEntregue: true,
	});
	const [values, setValues] = useState<LancarDatasDTO>({
		embarqueId: 0,
		previsaoEmbarque: null,
		dataHoraChegadaEmbarque: null,
		dataHoraEmbarcado: null,
		previsaoEntrega: null,
		dataHoraChegadaEntrega: null,
		dataHoraEntregue: null,
	});

	const [validator, setValidator] = useState({
		previsaoEmbarque: false,
		previsaoEntrega: false,
	});

	const [, editarData] = useService(
		() =>
			lancarDatasEmbarque({
				token: profile?.token || "",
				datas: values,
			}),
		{
			autoStart: false,
			onData: (data) => {
				onSuccess();
				onClose();
				showToast("Datas atualizadas com sucesso!", "success");
			},
			onError: (error) => {
				console.error("Erro ao salvar dados:", error);
			},
		},
	);

	const validateValues = useCallback(() => {
		if (!!tabelaRedux.row) {
			setValues(() => ({
				embarqueId: tabelaRedux.row.idEmbarque,
				previsaoEmbarque:
					!tabelaRedux.row.previsaoEmbarque ||
					isDataDoisMil(tabelaRedux.row.previsaoEmbarque)
						? null
						: tabelaRedux.row.previsaoEmbarque,
				previsaoEntrega:
					!tabelaRedux.row.previsaoEntrega ||
					isDataDoisMil(tabelaRedux.row.previsaoEntrega)
						? null
						: tabelaRedux.row.previsaoEntrega,
				dataHoraChegadaEmbarque:
					!tabelaRedux.row.dataHoraChegadaEmbarque ||
					isDataDoisMil(tabelaRedux.row.dataHoraChegadaEmbarque)
						? null
						: removeSeconds(tabelaRedux.row.dataHoraChegadaEmbarque),
				dataHoraChegadaEntrega:
					!tabelaRedux.row.dataHoraChegadaEntrega ||
					isDataDoisMil(tabelaRedux.row.dataHoraChegadaEntrega)
						? null
						: removeSeconds(tabelaRedux.row.dataHoraChegadaEntrega),
				dataHoraEntregue:
					!tabelaRedux.row.dataHoraEntregue ||
					isDataDoisMil(tabelaRedux.row.dataHoraEntregue)
						? null
						: removeSeconds(tabelaRedux.row.dataHoraEntregue),
				dataHoraEmbarcado:
					!tabelaRedux.row.dataHoraEmbarcado ||
					isDataDoisMil(tabelaRedux.row.dataHoraEmbarcado)
						? null
						: removeSeconds(tabelaRedux.row.dataHoraEmbarcado),
			}));
			setValidator({
				previsaoEmbarque:
					!tabelaRedux.row.previsaoEmbarque ||
					isDataDoisMil(tabelaRedux.row.previsaoEmbarque)
						? false
						: true,
				previsaoEntrega:
					!tabelaRedux.row.previsaoEntrega ||
					isDataDoisMil(tabelaRedux.row.previsaoEntrega)
						? false
						: true,
			});
		} else {
			setValidator({
				previsaoEmbarque: false,
				previsaoEntrega: false,
			});
		}
	}, [tabelaRedux.row]);

	const validacoes = useMemo(() => {
		return [
			{
				campo: "previsaoEmbarque",
				valor: values.previsaoEmbarque,
				setRange: (value) =>
					setRanges((current) => ({ ...current, previsaoEmbarque: value })),
				mensagem:
					"O campo Previsão de Embarque está fora do intervalo permitido.",
			},
			{
				campo: "previsaoEntrega",
				valor: values.previsaoEntrega,
				setRange: (value) =>
					setRanges((current) => ({ ...current, previsaoEntrega: value })),
				mensagem:
					"O campo Previsão de Entrega está fora do intervalo permitido.",
			},
			{
				campo: "dataHoraChegadaEmbarque",
				valor: values.dataHoraChegadaEmbarque,
				setRange: (value) =>
					setRanges((current) => ({
						...current,
						dataHoraChegadaEmbarque: value,
					})),
				mensagem:
					"O campo Dt.hr Chegada Embarque está fora do intervalo permitido.",
			},
			{
				campo: "dataHoraChegadaEntrega",
				valor: values.dataHoraChegadaEntrega,
				setRange: (value) =>
					setRanges((current) => ({
						...current,
						dataHoraChegadaEntrega: value,
					})),
				mensagem:
					"O campo Dt.hr Chegada Entrega está fora do intervalo permitido.",
			},
			{
				campo: "dataHoraEmbarcado",
				valor: values.dataHoraEmbarcado,
				setRange: (value) =>
					setRanges((current) => ({ ...current, dataHoraEmbarcado: value })),
				mensagem: "O campo Dt.hr Embarcado está fora do intervalo permitido.",
			},
			{
				campo: "dataHoraEntregue",
				valor: values.dataHoraEntregue,
				setRange: (value) =>
					setRanges((current) => ({ ...current, dataHoraEntregue: value })),
				mensagem: "O campo Dt.hr Entregue está fora do intervalo permitido.",
			},
		];
	}, [values]);

	const validarCampo = (valor, setRange, mensagem) => {
		if (!valor) {
			setRange(true);
			return true;
		}

		if (!isDateTimeInRange(valor)) {
			setRange(false);
			showToast(mensagem, "error");
			return false;
		}

		setRange(true);
		return true;
	};

	const validarRangeData = useCallback(() => {
		return validacoes.every(({ valor, setRange, mensagem }) =>
			validarCampo(valor, setRange, mensagem),
		);
	}, [validacoes]);

	const handleSave = useCallback(() => {
		if (!validarRangeData()) {
			return;
		}
		if (
			validator.previsaoEmbarque === true &&
			validator.previsaoEntrega === true &&
			!!values.previsaoEmbarque &&
			!!values.previsaoEntrega
		) {
			validateValues();
			editarData();
		} else {
			let texto = "O(s) campos a seguir são obrigatorios: ";
			if (!validator.previsaoEmbarque) {
				texto += "Previsão de Embarque, ";
			}
			if (!validator.previsaoEntrega) {
				texto += "Previsão de Entrega.";
			}
			showToast(texto, "error");
		}
	}, [validarRangeData, values, validator]);

	useEffect(() => {
		validateValues();
	}, [tabelaRedux.row]);

	return (
		<Modal isOpen={open} onClose={onClose}>
			<motion.div
				onClick={(e) => e.stopPropagation()}
				initial={{ opacity: 0, y: 50 }}
				exit={{ opacity: 0, y: 50 }}
				animate={{
					opacity: 1,
					y: 0,
				}}
				transition={{ duration: 0.3 }}
				className="modal-editar-datas-container"
			>
				<div className="content-container modal-editar-datas">
					<div>Editar datas:</div>
					<Separator size="xl" />
					<div className="modal-editar-datas-row">
						<div className="modal-editar-datas-coluna">
							<p className="input-label-emb">Previsão de Embarque:</p>
							<input
								type="datetime-local"
								className={
									validator.previsaoEmbarque && ranges.previsaoEmbarque
										? "input-emb"
										: "input-emb-erro"
								}
								style={{ width: "208px" }}
								onChange={(e) => {
									setRanges((current) => ({
										...current,
										previsaoEmbarque: true,
									}));
									setValues((current) => ({
										...current,
										previsaoEmbarque: e.target.value ? e.target.value : null,
									}));
									setValidator((current) => ({
										...current,
										previsaoEmbarque: e.target.value ? true : false,
									}));
								}}
								value={values.previsaoEmbarque || ""}
							/>
							{!validator.previsaoEmbarque && (
								<div className="error-text">Obrigatório!</div>
							)}
							{!ranges.previsaoEmbarque && validator.previsaoEmbarque && (
								<div className="error-text">Data inválida!</div>
							)}
						</div>
						<div className="modal-editar-datas-coluna">
							<p className="input-label-emb">Dt.hr Chegada Embarque:</p>
							<input
								type="datetime-local"
								className={
									ranges.dataHoraChegadaEmbarque
										? "input-emb"
										: "input-emb-erro"
								}
								style={{ width: "208px" }}
								onChange={(e) => {
									setRanges((current) => ({
										...current,
										dataHoraChegadaEmbarque: true,
									}));
									setValues((current) => ({
										...current,
										dataHoraChegadaEmbarque: e.target.value
											? e.target.value
											: null,
									}));
								}}
								value={values.dataHoraChegadaEmbarque || ""}
							/>
							{!ranges.dataHoraChegadaEmbarque && (
								<div className="error-text">Data inválida!</div>
							)}
						</div>
						<div className="modal-editar-datas-coluna">
							<p className="input-label-emb">Dt.hr Embarcado:</p>
							<input
								type="datetime-local"
								className={
									ranges.dataHoraEmbarcado ? "input-emb" : "input-emb-erro"
								}
								style={{ width: "208px" }}
								onChange={(e) => {
									setRanges((current) => ({
										...current,
										dataHoraEmbarcado: true,
									}));
									setValues((current) => ({
										...current,
										dataHoraEmbarcado: e.target.value ? e.target.value : null,
									}));
								}}
								value={values.dataHoraEmbarcado || ""}
							/>
							{!ranges.dataHoraEmbarcado && (
								<div className="error-text">Data inválida!</div>
							)}
						</div>
					</div>
					<Separator size="xl" />
					<div className="modal-editar-datas-row">
						<div className="modal-editar-datas-coluna">
							<div>
								<p className="input-label-emb">Previsão de Entrega:</p>
								<input
									type="datetime-local"
									className={
										validator.previsaoEntrega && ranges.previsaoEntrega
											? "input-emb"
											: "input-emb-erro"
									}
									style={{ width: "208px" }}
									onChange={(e) => {
										setValues((current) => ({
											...current,
											previsaoEntrega: e.target.value ? e.target.value : null,
										}));
										setRanges((current) => ({
											...current,
											previsaoEntrega: true,
										}));
										setValidator((current) => ({
											...current,
											previsaoEntrega: e.target.value ? true : false,
										}));
									}}
									value={values.previsaoEntrega || ""}
								/>
								{!validator.previsaoEntrega && (
									<div className="error-text">Obrigatório!</div>
								)}
								{!ranges.previsaoEntrega && !validator.previsaoEntrega && (
									<div className="error-text">Data inválida!</div>
								)}
							</div>
						</div>
						<div className="modal-editar-datas-coluna">
							<p className="input-label-emb">Dt.hr Chegada Entrega:</p>
							<input
								type="datetime-local"
								className={
									ranges.dataHoraChegadaEntrega ? "input-emb" : "input-emb-erro"
								}
								style={{ width: "208px" }}
								onChange={(e) => {
									setRanges((current) => ({
										...current,
										dataHoraChegadaEntrega: true,
									}));
									setValues((current) => ({
										...current,
										dataHoraChegadaEntrega: e.target.value
											? e.target.value
											: null,
									}));
								}}
								value={values.dataHoraChegadaEntrega || ""}
							/>
							{!ranges.dataHoraChegadaEntrega && (
								<div className="error-text">Data inválida!</div>
							)}
						</div>
						<div className="modal-editar-datas-coluna">
							<p className="input-label-emb">Dt.hr Entregue:</p>
							<input
								type="datetime-local"
								className={
									ranges.dataHoraEntregue ? "input-emb" : "input-emb-erro"
								}
								style={{ width: "208px" }}
								onChange={(e) => {
									setRanges((current) => ({
										...current,
										dataHoraEntregue: true,
									}));
									setValues((current) => ({
										...current,
										dataHoraEntregue: e.target.value ? e.target.value : null,
									}));
								}}
								value={values.dataHoraEntregue || ""}
							/>
							{!ranges.dataHoraEntregue && (
								<div className="error-text">Data inválida!</div>
							)}
						</div>
					</div>
				</div>
				<div className="footer-container">
					<CustomButtonV2
						classCss="custom-button-cadastrar"
						classText="button-texto-cadastrar"
						titulo="Salvar"
						cor="#0946B8"
						onClick={handleSave}
					/>
					<CustomButtonV2
						classCss="custom-button-cancelar"
						classText="button-texto-cancelar"
						titulo="Cancelar"
						cor="#0946B8"
						onClick={onClose}
					/>
				</div>
			</motion.div>
		</Modal>
	);
}

export default memo(ModalEditarDatas);
